import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { PageHeader } from "@/components/page-header";
import { orpc } from "@/lib/orpc";
import { DiscountCodeEditor, type DiscountCodeValues } from "./-components/discount-code-form";

const groupsQuery = () => orpc.price.listCustomerGroups.queryOptions();

export const Route = createFileRoute("/_admin/discount-code/new")({
	loader: ({ context }) => context.queryClient.ensureQueryData(groupsQuery()),
	component: NewDiscountCodePage,
});

/** Make a code; once saved, the editor of the new code takes over. */
function NewDiscountCodePage() {
	const { t } = useTranslation("discountCode");
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { data: settings } = useSuspenseQuery(groupsQuery());
	const create = useMutation({
		...orpc.discountCode.create.mutationOptions(),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.discountCode.key() }),
	});

	const onSubmit = async (values: DiscountCodeValues) => {
		const code = await create.mutateAsync(values);
		await navigate({ to: "/discount-code/$id", params: { id: code.id }, replace: true });
	};

	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/discount-code">{t("title")}</BackLink>}
				title={t("new.title")}
				description={t("new.description")}
			/>
			<DiscountCodeEditor
				customerGroups={settings.groups}
				submitLabel={t("new.submit")}
				onSubmit={onSubmit}
			/>
		</div>
	);
}
